import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";

import { SearchStandardsGroup } from "../common/api";
import { COMMON_CORE_STATE } from "../common/standards";
import {
  COLOR_BLACK,
  COLOR_BLUE,
  COLOR_SURFACE_CONTAINER,
  COLOR_TEXT_SECONDARY,
} from "../common/colors";

// Explains the academic-standards code(s) a search query named, above the
// results (rendered only behind the client-side "standards" flag). Collapsed
// by default — the full descriptions can push the actual results below the
// fold — showing just the heading and a one-line code summary; tapping the
// header expands one entry per distinct description, with a chip per state
// ("CC" = Common Core) it comes from.

const Banner = styled.div`
  margin-bottom: 24px;
  padding: 12px 16px;
  border-radius: 16px;
  background: ${COLOR_SURFACE_CONTAINER};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
`;

const BannerHeading = styled.span`
  font-weight: 600;
  font-size: 16px;
  color: ${COLOR_BLACK};
  white-space: nowrap;
`;

const CollapsedSummary = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 14px;
  color: ${COLOR_TEXT_SECONDARY};
`;

const Chevron = styled.span<{ isExpanded: boolean }>`
  color: ${COLOR_TEXT_SECONDARY};
  transition: transform 0.15s ease;
  transform: rotate(${(p) => (p.isExpanded ? 180 : 0)}deg);
`;

const Groups = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
`;

const GroupCodes = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: ${COLOR_BLACK};
`;

// A family search ("3R") explains several member codes; each doubles as a
// shortcut to the narrower search for exactly that code.
const CodeLink = styled.span`
  color: ${COLOR_BLUE};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const GroupMeta = styled.div`
  font-size: 13px;
  color: ${COLOR_TEXT_SECONDARY};
`;

const GroupDescription = styled.div`
  font-size: 14px;
  color: ${COLOR_BLACK};
  /* Some standards are bullet lists; the server keeps their newlines. */
  white-space: pre-line;
`;

const StateChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
`;

const StateChip = styled.span`
  border-radius: 10px;
  border: 1px solid ${COLOR_TEXT_SECONDARY};
  padding: 1px 8px;
  font-size: 12px;
  color: ${COLOR_TEXT_SECONDARY};
  cursor: default;
`;

interface Props {
  groups: SearchStandardsGroup[];
  // Runs a fresh search for the tapped code (parent owns the search state).
  onSearchCode: (code: string) => void;
}

const SearchStandardsBanner = ({ groups, onSearchCode }: Props) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = React.useState(false);

  const summaryCodes = Array.from(
    new Set(groups.flatMap((group) => group.codes.map((c) => c.code)))
  ).join(", ");

  return (
    <Banner>
      <HeaderRow
        role="button"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((expanded) => !expanded)}
      >
        <BannerHeading>{t("standards_banner_heading")}</BannerHeading>
        {!isExpanded && <CollapsedSummary>{summaryCodes}</CollapsedSummary>}
        <Chevron isExpanded={isExpanded}>
          <FontAwesomeIcon icon={faChevronDown} size="sm" />
        </Chevron>
      </HeaderRow>
      {isExpanded && (
        <Groups>
          {groups.map((group) => {
            const codes = Array.from(new Set(group.codes.map((c) => c.code)));
            const states = Array.from(new Set(group.codes.map((c) => c.state)));
            return (
              <div key={group.description}>
                <GroupCodes>
                  {codes.map((code, index) => (
                    <React.Fragment key={code}>
                      {index > 0 && ", "}
                      <CodeLink role="button" onClick={() => onSearchCode(code)}>
                        {code}
                      </CodeLink>
                    </React.Fragment>
                  ))}
                </GroupCodes>
                {(group.subject || group.grades) && (
                  <GroupMeta>
                    {group.grades
                      ? t("standards_banner_group_meta", {
                          subject: group.subject,
                          grades: group.grades,
                        })
                      : group.subject}
                  </GroupMeta>
                )}
                <GroupDescription>{group.description}</GroupDescription>
                <StateChips>
                  {states.map((state) => (
                    <StateChip
                      key={state}
                      title={
                        state === COMMON_CORE_STATE
                          ? t("standards_common_core_label")
                          : undefined
                      }
                    >
                      {state}
                    </StateChip>
                  ))}
                </StateChips>
              </div>
            );
          })}
        </Groups>
      )}
    </Banner>
  );
};

export default SearchStandardsBanner;
